import { faCircleCheck, faFilePen, faLock, faStore, faTriangleExclamation, faUserSecret } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-native-fontawesome';
import { useQuery } from '@tanstack/react-query';
import { Pressable, View } from 'react-native';
import {
  CompetitorCircuitRole,
  CompetitorCircuitStepResponse,
  MissionAssignationStatus,
  MyMissionClient,
} from '../../api/api.g';
import useApi from '../../hooks/api-hook';
import useStyles from '../../hooks/styles-hook';
import useCustomTheme from '../../hooks/theme-hook';
import useTranslation from '../../hooks/translation-hook';
import Label, { LabelType } from '../common/Label';
import Loading from '../common/Loading';
import CustomButton from '../common/CustomButton';
import useCompetitorCircuitQuestionnaire from '../../hooks/competitor-circuit-questionnaire-hook';
import { canCancelCircuit } from '../../helpers/competitorCircuitSurvey';

type Props = {
  circuitId: string;
  onCancel(): void;
};

const CompetitorCircuitPanel = ({ circuitId, onCancel }: Props) => {
  const t = useTranslation();
  const theme = useCustomTheme();
  const styles = useStyles((section) => section.myMissions.newVersion.competitorCircuit);
  const api = useApi(MyMissionClient);

  const circuit = useQuery({
    queryKey: ['competitor-circuit', circuitId],
    queryFn: () => api.getCompetitorCircuit(circuitId, api.tokenSource?.token),
    staleTime: 0,
    refetchOnMount: 'always',
  });
  const mainMissionAssignationId = circuit.data?.steps?.find(step => step.role === CompetitorCircuitRole.Main)?.missionAssignationId;
  const { openQuestionnaire, openingId, errorKey } = useCompetitorCircuitQuestionnaire(circuitId, mainMissionAssignationId);

  if (circuit.isLoading) return <Loading />;
  if (circuit.isError && !circuit.data) return <View style={styles.container}>
    <Label>{t('competitor-circuit-load-failed')}</Label>
    <CustomButton title={t('try-again')} onPress={() => void circuit.refetch()} />
  </View>;
  if (!circuit.data) return <></>;

  const canOpen = (status?: MissionAssignationStatus) =>
    status === MissionAssignationStatus.Assigned ||
    status === MissionAssignationStatus.ValidationRejected ||
    status === MissionAssignationStatus.PrevalidationFailed;

  const hasIssue = (status?: MissionAssignationStatus) =>
    status === MissionAssignationStatus.ValidationRejected ||
    status === MissionAssignationStatus.PrevalidationFailed ||
    status === MissionAssignationStatus.FraudDetected;

  const assignedSteps = circuit.data.steps ?? [];
  const hasFollowUp = assignedSteps.some((step) => step.role === CompetitorCircuitRole.FollowUp);
  const displaySteps: { step?: CompetitorCircuitStepResponse; isPending: boolean }[] = [
    ...assignedSteps.map((step) => ({ step, isPending: false })),
    ...(hasFollowUp ? [] : [{ isPending: true }]),
  ];
  const completedSteps = circuit.data.completedSteps ?? 0;
  const totalSteps = Math.max(circuit.data.totalSteps ?? 0, displaySteps.length);
  const progressPercentage = totalSteps > 0 ? Math.min(100, (completedSteps / totalSteps) * 100) : 0;
  const isCancellable = canCancelCircuit(circuit.data);

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <View style={styles.headerRow}>
          <View style={styles.headerIcon}>
            <FontAwesomeIcon icon={faUserSecret} size={20} color={theme.timelineIcon as string} />
          </View>
          <View style={styles.headerContent}>
            <Label variant={LabelType.titleDark}>{t('competitor-circuit')}</Label>
            <Label style={styles.progressText}>
              {t('competitor-circuit-progress', { completed: completedSteps, total: totalSteps })}
            </Label>
          </View>
          <Label style={styles.progressCount}>
            {completedSteps}/{totalSteps}
          </Label>
        </View>
        <View style={styles.progressTrack}>
          <View style={[styles.progressFill, { width: `${progressPercentage}%` }]} />
        </View>
      </View>

      <View style={styles.steps}>
        {displaySteps.map(({ step, isPending }) => {
          const role = step?.role ?? CompetitorCircuitRole.FollowUp;
          const isIssue = !isPending && hasIssue(step?.status);
          const isOpening = !!step && openingId === step.missionAssignationId;
          const questionnaireTitle = t(step?.status === MissionAssignationStatus.ValidationRejected || step?.status === MissionAssignationStatus.PrevalidationFailed
            ? 'Correct-my-questionnaire' : 'open-questionnaire');

          return (
            <View
              key={step?.missionAssignationId ?? 'pending-comparison-follow-up'}
              style={[styles.step, isPending ? styles.stepLocked : undefined]}
            >
              <View style={styles.stepRow}>
                <View
                  style={[
                    styles.stepIcon,
                    isIssue ? { borderColor: theme.text.error } : undefined,
                    isPending ? styles.stepIconLocked : undefined,
                  ]}
                >
                  {isPending ? (
                    <FontAwesomeIcon icon={faLock} size={13} color={theme.text.titleLight as string} />
                  ) : isIssue ? (
                    <FontAwesomeIcon icon={faTriangleExclamation} size={13} color={theme.text.error as string} />
                  ) : step?.submittedDateUtc ? (
                    <FontAwesomeIcon
                      icon={faCircleCheck}
                      size={14}
                      color={theme.secondaryLight as string}
                    />
                  ) : (
                    <FontAwesomeIcon
                      icon={role === CompetitorCircuitRole.FollowUp ? faFilePen : faStore}
                      size={16}
                      color={theme.text.titleDark as string}
                    />
                  )}
                </View>

                <View style={styles.stepBody}>
                  <Label style={styles.stepTitle} numberOfLines={2}>
                    {role === CompetitorCircuitRole.FollowUp ? t('competitor-circuit-follow-up') : step?.name}
                  </Label>
                  {isPending && (
                    <Label style={styles.stepLockedText}>{t('competitor-circuit-follow-up-available-later')}</Label>
                  )}
                  {!!step?.address && (
                    <Label style={styles.stepAddress} numberOfLines={2}>
                      {step.address}
                    </Label>
                  )}
                </View>

                {!isPending && (
                  <View style={styles.stepMeta}>
                    {step?.status !== undefined && (
                      <View style={[styles.statusBadge, isIssue ? { backgroundColor: theme.colorErrorPrimaryBackground } : undefined]}>
                        <Label style={[styles.statusText, isIssue ? { color: theme.text.error } : undefined]}>
                          {t(MissionAssignationStatus[step.status])}
                        </Label>
                      </View>
                    )}
                  </View>
                )}
              </View>

              {step && canOpen(step.status) && (
                <View style={styles.questionnaireAction} accessibilityState={{ busy: isOpening }}>
                  {isOpening ? <Loading style={styles.questionnaireLoading} accessibilityRole="progressbar" /> : (
                    <Pressable
                      accessibilityRole="button"
                      accessibilityLabel={questionnaireTitle}
                      disabled={!!openingId || !mainMissionAssignationId}
                      accessibilityState={{ disabled: !!openingId || !mainMissionAssignationId }}
                      onPress={() => void openQuestionnaire(step)}
                      style={styles.questionnaireButton}
                    >
                      <Label style={styles.questionnaireButtonText}>{questionnaireTitle}</Label>
                    </Pressable>
                  )}
                </View>
              )}
            </View>
          );
        })}
      </View>
      {!!errorKey && <Label style={{ color: theme.text.error }}>{t(errorKey)}</Label>}

      {isCancellable && (
        <View style={styles.footer}>
          <View style={styles.warningRow}>
            <FontAwesomeIcon icon={faTriangleExclamation} size={14} color={theme.text.error as string} />
            <Label style={styles.warningText}>{t('competitor-circuit-cancel-warning')}</Label>
          </View>
          <Pressable accessibilityRole="button" onPress={onCancel} style={styles.cancelButton}>
            <Label style={styles.cancelText}>{t('cancel-competitor-circuit')}</Label>
          </Pressable>
        </View>
      )}
    </View>
  );
};

export default CompetitorCircuitPanel;
